import React, { useState, useEffect, useCallback, useRef } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import './App.css';
import Login from './pages/Login';
import { rememberSessionEnd, SESSION_NOTICE_KEY } from './utils/session';
import Dashboard from './pages/Dashboard';
import Upload from './pages/Upload';
import Matching from './pages/Matching';
import Results from './pages/Results';
import Settings from './pages/Settings';
import AdminOrders from './pages/AdminOrders';
import Onboarding from './pages/Onboarding';
import PlatformAdmin from './pages/PlatformAdmin';
// Alessa-специфика (несколько прайсов/остатков) — code-split: пользователи других
// профилей никогда не скачивают этот чанк, он даже не запрашивается с сервера.
const PriceLists = React.lazy(() => import('./pages/PriceLists'));

const API_URL = process.env.REACT_APP_API_URL || '';

// Защищённый роут для админов
function AdminRoute({ children, userRole }) {
  if (userRole !== 'admin') {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <h2>Доступ запрещен</h2>
        <p>Требуются права администратора</p>
      </div>
    );
  }
  return children;
}

// Компонент навигации (отдельно, чтобы использовать useNavigate)
function AppContent() {
  const navigate = useNavigate();
  const location = useLocation();

  const [token, setToken] = useState(localStorage.getItem('token'));
  const [userRole, setUserRole] = useState(localStorage.getItem('userRole'));
  const [onboardingStatus, setOnboardingStatus] = useState(localStorage.getItem('onboardingStatus'));
  const [currentOrderId, setCurrentOrderId] = useState(
    localStorage.getItem('currentOrderId') ? parseInt(localStorage.getItem('currentOrderId')) : null
  );
  const [matchingData, setMatchingData] = useState(null);
  const [completingOnboarding, setCompletingOnboarding] = useState(false);
  const [completionMessage, setCompletionMessage] = useState('');
  const [uiProfile, setUiProfile] = useState(null);
  const [openaiQuotaExceeded, setOpenaiQuotaExceeded] = useState(false);
  const [sessionReady, setSessionReady] = useState(!localStorage.getItem('token'));
  // Синхронный флаг (не state!) против двойного клика по «Готово»: React применяет
  // disabled={completingOnboarding} только на СЛЕДУЮЩЕМ рендере, поэтому быстрый
  // двойной клик успевает отправить два одновременных POST /api/onboarding/complete
  // до того, как кнопка визуально задизейблится (тот же класс бага, что был в Login).
  const completingRef = useRef(false);

  // Сохраняем orderId в localStorage при изменении
  useEffect(() => {
    if (currentOrderId) {
      localStorage.setItem('currentOrderId', currentOrderId.toString());
    }
  }, [currentOrderId]);

  // При наличии токена — подтягиваем актуальный статус онбординга (мог измениться
  // с прошлой сессии, например администратор компании его завершил)
  const refreshMe = useCallback(async () => {
    if (!token) {
      setSessionReady(true);
      return;
    }
    try {
      const res = await fetch(`${API_URL}/api/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.status === 401 || res.status === 404) {
        // 401 — токен недействителен; 404 — пользователь удалён. В обоих
        // случаях кешированное состояние (роль/статус онбординга) устарело.
        await rememberSessionEnd(res);
        handleLogout();
        return;
      }
      if (res.ok) {
        const data = await res.json();
        setUserRole(data.role);
        localStorage.setItem('userRole', data.role);
        setOnboardingStatus(data.onboarding_status);
        setUiProfile(data.ui_profile || 'saas');
        setOpenaiQuotaExceeded(!!data.openai_quota_exceeded);
        if (data.onboarding_status) {
          localStorage.setItem('onboardingStatus', data.onboarding_status);
        }
      }
    } catch (err) {
      // молча игнорируем — останемся с закешированным значением
    } finally {
      setSessionReady(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => { refreshMe(); }, [refreshMe]);

  // Периодически перепроверяем сессию: однопользовательский вход (см. auth.py) означает,
  // что токен этой вкладки может стать недействительным в любой момент — если под этой же
  // учётной записью вошли где-то ещё. Без опроса вкладка узнала бы об этом только на
  // следующем действии пользователя (клик/запрос), а до этого молча показывала бы ошибки.
  useEffect(() => {
    if (!token) return;
    const interval = setInterval(refreshMe, 60000);
    return () => clearInterval(interval);
  }, [token, refreshMe]);

  const handleLogin = (newToken, userId, role, companyId, newOnboardingStatus, newUiProfile) => {
    sessionStorage.removeItem(SESSION_NOTICE_KEY);
    localStorage.setItem('token', newToken);
    localStorage.setItem('userId', userId);
    localStorage.setItem('userRole', role);
    if (companyId != null) localStorage.setItem('companyId', companyId);
    if (newOnboardingStatus) localStorage.setItem('onboardingStatus', newOnboardingStatus);
    else localStorage.removeItem('onboardingStatus');
    setToken(newToken);
    setUserRole(role);
    setOnboardingStatus(newOnboardingStatus || null);
    setUiProfile(newUiProfile || 'saas');
    setSessionReady(true);
    // Если компания уже на проверке (GPT сгенерировал конфигурацию, но её ещё
    // не подтвердили) — ведём сразу на экран проверки, а не на /upload.
    navigate(newOnboardingStatus === 'needs_review' ? '/settings?tab=filters' : '/upload');
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('userId');
    localStorage.removeItem('userRole');
    localStorage.removeItem('companyId');
    localStorage.removeItem('onboardingStatus');
    setToken(null);
    setUserRole(null);
    setOnboardingStatus(null);
    setUiProfile(null);
    setSessionReady(true);
    navigate('/login');
  };

  const handleOnboardingComplete = () => {
    setOnboardingStatus('ready');
    localStorage.setItem('onboardingStatus', 'ready');
    navigate('/upload');
  };

  // Пересканирование номенклатуры на завершении онбординга идёт в фоне (может занять
  // время на больших каталогах) — опрашиваем статус, пока backend не отрапортует done/error.
  // Раньше запрос был синхронным и кнопка зависала без обратной связи на несколько минут.
  const handleFinishOnboarding = async () => {
    if (completingRef.current) return;
    completingRef.current = true;
    setCompletingOnboarding(true);
    setCompletionMessage('Запуск...');
    try {
      const res = await fetch(`${API_URL}/api/onboarding/complete`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok && res.status !== 409) {
        throw new Error(data.detail || 'Не удалось завершить онбординг');
      }

      // Поллинг статуса до done/error
      // eslint-disable-next-line no-constant-condition
      while (true) {
        await new Promise((r) => setTimeout(r, 2000));
        const statusRes = await fetch(`${API_URL}/api/onboarding/status`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!statusRes.ok) continue;
        const statusData = await statusRes.json();
        const completion = statusData.completion;
        if (!completion) continue;
        setCompletionMessage(completion.message || '');
        if (completion.status === 'done') {
          handleOnboardingComplete();
          break;
        }
        if (completion.status === 'error') {
          throw new Error(completion.message || 'Не удалось завершить онбординг');
        }
      }
    } catch (err) {
      alert(err.message);
    } finally {
      completingRef.current = false;
      setCompletingOnboarding(false);
      setCompletionMessage('');
    }
  };

  // Мастер онбординга сам опрашивает свой прогресс — как только его локальный
  // статус меняется (например GPT закончил анализ и статус стал needs_review),
  // подтягиваем это наверх, чтобы перестать рендерить сам мастер.
  const handleOnboardingStatusChange = (newStatus) => {
    setOnboardingStatus((prev) => {
      if (prev === newStatus) return prev;
      localStorage.setItem('onboardingStatus', newStatus);
      // Именно в момент перехода "мастер -> обычное приложение" (GPT только что
      // закончил анализ) сразу открываем экран проверки, а не оставляем
      // пользователя на той странице, где он случайно оказался — иначе баннер
      // легко не заметить и подумать, что шаг проверки вообще пропущен.
      if (newStatus === 'needs_review' && prev !== 'needs_review') {
        navigate('/settings?tab=filters');
      }
      return newStatus;
    });
  };

  const handleNavigate = (page, orderId = null) => {
    if (orderId) setCurrentOrderId(orderId);
    navigate(`/${page}`);
  };

  const handleMatchingStart = (data) => {
    setMatchingData(data);
    if (data.order_id) {
      setCurrentOrderId(data.order_id);
      localStorage.setItem('currentOrderId', data.order_id.toString());
    }
    navigate('/results');
  };

  const handleMatchingComplete = () => {
    navigate('/results');
  };

  // Если нет токена - показываем только логин
  if (!token) {
    return (
      <Routes>
        <Route path="/login" element={<Login onLogin={handleLogin} />} />
        <Route path="*" element={<Navigate to={{ pathname: '/login', search: location.search }} replace />} />
      </Routes>
    );
  }

  // Never render one customer's UI from stale localStorage while /auth/me is
  // still resolving the authoritative deployment profile.
  if (!sessionReady || !uiProfile) {
    return <div className="app-profile-loading">Загрузка…</div>;
  }

  // Платформенный супер-админ живёт вне компаний — своя мини-панель, без обычной навигации
  if (uiProfile === 'saas' && userRole === 'platform_admin') {
    return <PlatformAdmin token={token} onLogout={handleLogout} />;
  }

  // Компания ещё не прошла онбординг (ключ/номенклатура/анализ GPT) — блокируем всё остальное
  if (uiProfile === 'saas' && onboardingStatus && onboardingStatus !== 'ready' && onboardingStatus !== 'needs_review') {
    return <Onboarding token={token} onLogout={handleLogout} onStatusChange={handleOnboardingStatusChange} />;
  }

  // Определяем текущую страницу для подсветки в навигации
  const currentPath = location.pathname.replace('/', '') || 'upload';

  return (
    <div className="app">
      <header className="app-header">
        <div className="header-content">
          <h1>ИИ:Матчер</h1>
          <button className="logout-btn" onClick={handleLogout}>
            Выход
          </button>
        </div>
      </header>

      {openaiQuotaExceeded && (
        <div className="onboarding-review-banner" style={{ background: '#fdecea', borderColor: '#e53935', color: '#b71c1c' }}>
          <span>
            ⚠️ Деньги на API GPT закончились — необходимо пополнить баланс OpenAI.
            Сопоставление заказов и распознавание не будут работать, пока баланс не пополнен.
          </span>
        </div>
      )}

      {uiProfile === 'saas' && onboardingStatus === 'needs_review' && (
        <div className="onboarding-review-banner">
          <span>
            Настройка почти завершена — проверьте категории, характеристики и промпты в «Настройках»,
            затем нажмите «Готово».
          </span>
          <button
            type="button"
            disabled={completingOnboarding}
            onClick={handleFinishOnboarding}
          >
            {completingOnboarding ? (completionMessage || 'Завершение...') : 'Готово, начать работу'}
          </button>
        </div>
      )}

      <nav className="app-nav">
        <button
          className={currentPath === 'dashboard' ? 'active' : ''}
          onClick={() => navigate('/dashboard')}
        >
          Главная
        </button>
        <button
          className={currentPath === 'upload' ? 'active' : ''}
          onClick={() => navigate('/upload')}
        >
          Загрузить заказ
        </button>
        <button
          className={currentPath === 'admin-orders' ? 'active' : ''}
          onClick={() => navigate('/admin-orders')}
        >
          История
        </button>
        {(userRole === 'admin' || (uiProfile === 'alessa' && userRole === 'price_uploader')) && (
          <button
            // Раньше startsWith('settings') подсвечивал эту кнопку и на /settings/prices —
            // там у Алессы есть СВОЯ отдельная кнопка «Прайсы» ниже, поэтому «Настройки»
            // должна светиться только на том пути, куда она сама ведёт (у price_uploader
            // это /settings/prices — единственный доступный ему раздел, у остальных /settings).
            className={currentPath === (userRole === 'price_uploader' ? 'settings/prices' : 'settings') ? 'active' : ''}
            onClick={() => navigate(userRole === 'price_uploader' ? '/settings/prices' : '/settings')}
          >
            Настройки
          </button>
        )}
        {uiProfile === 'alessa' && userRole === 'admin' && (
          <button
            className={currentPath === 'settings/prices' ? 'active' : ''}
            onClick={() => navigate('/settings/prices')}
          >
            Прайсы
          </button>
        )}
      </nav>

      <main className="app-main">
        <Routes>
          <Route
            path="/dashboard"
            element={
              <Dashboard
                token={token}
                onNavigate={handleNavigate}
                onMatchingStart={handleMatchingStart}
              />
            }
          />
          <Route
            path="/upload"
            element={
              <Upload
                token={token}
                userRole={userRole}
                uiProfile={uiProfile}
                onNavigate={handleNavigate}
                onMatchingStart={handleMatchingStart}
              />
            }
          />
          <Route
            path="/matching"
            element={
              matchingData ? (
                <Matching
                  token={token}
                  orderId={currentOrderId}
                  matchingData={matchingData}
                  onComplete={handleMatchingComplete}
                  onNavigate={handleNavigate}
                />
              ) : (
                <Navigate to="/upload" replace />
              )
            }
          />
          <Route
            path="/results"
            element={
              <Results
                token={token}
                orderId={currentOrderId}
                onNavigate={handleNavigate}
                userRole={userRole}
              />
            }
          />
          <Route
            path="/settings"
            element={
              <AdminRoute userRole={userRole}>
                <Settings
                  token={token}
                  onNavigate={handleNavigate}
                  onboardingStatus={onboardingStatus}
                  uiProfile={uiProfile}
                />
              </AdminRoute>
            }
          />
          <Route
            path="/settings/prices"
            element={(uiProfile === 'alessa' && (userRole === 'admin' || userRole === 'price_uploader'))
              ? <React.Suspense fallback={null}><PriceLists token={token} userRole={userRole} /></React.Suspense>
              : <Navigate to="/upload" replace />}
          />
          <Route
            path="/admin-orders"
            element={
              <AdminOrders
                token={token}
                userRole={userRole}
                onNavigate={handleNavigate}
              />
            }
          />
          <Route path="/login" element={<Navigate to="/upload" replace />} />
          <Route path="/" element={<Navigate to="/upload" replace />} />
          <Route
            path="*"
            element={
              <div style={{ padding: '40px', textAlign: 'center' }}>
                <h2>Страница не найдена</h2>
                <button onClick={() => navigate('/upload')} style={{ marginTop: '20px', padding: '10px 20px', cursor: 'pointer' }}>
                  Вернуться на главную
                </button>
              </div>
            }
          />
        </Routes>
      </main>

      <footer className="app-footer">
        <p>ИИ:Матчер</p>
      </footer>
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AppContent />
    </BrowserRouter>
  );
}

export default App;
